import { useCallback, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { buildGraph, PROJECTS } from "@/components/knowledge/graphData";
import { buildIndex, clamp01, drift, getFocus, tourAt, useGraphClock, useNarrow } from "@/components/knowledge/graphMotion";
import { GraphEdges, GraphParticles } from "@/components/knowledge/GraphEdges";
import { GraphNodes } from "@/components/knowledge/GraphNodes";
import { KnowledgeControls, KnowledgePanel } from "@/components/knowledge/KnowledgePanel";

/**
 * Animated, interactive knowledge map of MUCCI's four learning pathways.
 * Subjects grow branches of knowledge concepts; interdisciplinary projects connect across pathways.
 */
const THEME_IDS = PROJECTS.map((p) => p.id);

export const KnowledgeGraph = () => {
  const reduce = useReducedMotion();
  const wrapRef = useRef(null);
  const narrow = useNarrow();
  const graph = useMemo(() => buildGraph(narrow ? "tall" : "wide"), [narrow]);
  const index = useMemo(() => buildIndex(graph), [graph]);
  const t = useGraphClock(wrapRef, reduce);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);

  const select = useCallback((id) => { setHover(null); setSelected((cur) => (cur === id ? null : id)); }, []);
  const reset = useCallback(() => { setHover(null); setSelected(null); }, []);

  const focus = useMemo(
    () => (hover ? getFocus(hover, "hover", graph, index) : getFocus(selected, "select", graph, index)),
    [hover, selected, graph, index],
  );

  const amp = reduce ? 0 : clamp01((t - 1) / 3);
  const tour = tourAt(t, THEME_IDS);
  const pos = {};
  graph.nodes.forEach((n) => { pos[n.id] = drift(n, t, amp); });
  const { w, h } = graph.L;

  return (
    <div ref={wrapRef} className="space-y-5" data-testid="knowledge-graph" onKeyDown={(e) => e.key === "Escape" && reset()}>
      <KnowledgeControls selected={selected} onReset={reset} />
      <div className="card-soft overflow-hidden bg-white">
        <svg viewBox={`0 0 ${w} ${h}`} className="block h-auto w-full select-none" role="group"
          aria-label="Interactive knowledge map: Science Inquiry, Social Studies & Humanities, Engineering & Design and Critical Thinking & Global Issues, connected through real-world interdisciplinary themes">
          <rect width={w} height={h} fill="#FFFFFF" onClick={reset} data-testid="kg-background" />
          <GraphEdges graph={graph} pos={pos} t={t} focus={focus} tour={tour} />
          {!reduce && <GraphParticles graph={graph} pos={pos} t={t} focus={focus} tour={tour} />}
          <GraphNodes graph={graph} pos={pos} t={t} focus={focus} tour={tour} hover={hover} selected={selected} reduce={reduce}
            onHover={setHover} onSelect={select} />
        </svg>
      </div>
      <KnowledgePanel selected={hover || selected} graph={graph} onSelect={select} />
    </div>
  );
};
